import { createRootRoute, createRoute, createRouter, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { dataBR, ESPECIES_INFO } from "@vetbolso/core";
import { COR_ESPECIE, Icone, Logo, type NomeIcone } from "@vetbolso/ui";
import { useEffect, useRef } from "react";
import { useApp } from "./estado.tsx";
import { Folhas } from "./folhas.tsx";
import { TelaPortao } from "./portao.tsx";
import { TelaMapa } from "./telas/mapas-rotas.tsx";
import { TelaDoses, TelaInicio, TelaMais, TelaReceita, TelaToxicos } from "./telas/principais.tsx";
import { TelaSub } from "./telas/subs.tsx";

const ABAS: [string, string, NomeIcone][] = [
  ["/", "Início", "home"],
  ["/doses", "Doses", "syringe"],
  ["/toxicos", "Tóxicos", "alert"],
  ["/receita", "Receita", "file"],
  ["/mais", "Mais", "grid"],
];

function Casca() {
  const app = useApp();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const main = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: rola para o topo a cada tela nova
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [caminho, app.especie]);

  if (app.estado.fase !== "pronta") return <TelaPortao />;
  const { base, online } = app.estado;
  // Subtelas acendem "Mais"; a de fontes pode vir do Início.
  const aba = caminho.startsWith("/m/") ? "/mais" : caminho;

  return (
    <div className={`app${caminho.startsWith("/m/mapas/") ? " app-larga" : ""}`}>
      <div className="app-head">
        <div className="app-head-row">
          <span className="lockup">
            <Logo tamanho={30} />
            <span className="word">
              Vet<b>Bolso</b>
            </span>
          </span>
          <Link
            to="/m/$sub"
            params={{ sub: "conta" }}
            className={`status${online ? "" : " off"}`}
            title={online ? "Sincronizada" : "Sem conexão: usando a base salva"}
          >
            {online ? "" : "OFFLINE · "}BASE {dataBR(base.meta?.conferidoEm)}
          </Link>
        </div>
        {/* biome-ignore lint/a11y/useSemanticElements: grupo de botões de alternância, como no protótipo */}
        <div className="species" role="group" aria-label="Espécie">
          {ESPECIES_INFO.map((e) => (
            <button key={e.id} type="button" aria-pressed={app.especie === e.id} onClick={() => app.setEspecie(e.id)}>
              <i style={{ background: COR_ESPECIE[e.id] }} />
              {e.nome}
            </button>
          ))}
        </div>
      </div>
      <main className="app-main" ref={main}>
        <Outlet />
      </main>
      <nav className="tabbar" aria-label="Navegação do app">
        {ABAS.map(([to, n, i]) => (
          <Link
            key={to}
            to={to}
            aria-current={aba === to ? "page" : undefined}
            onClick={() => app.fecharTodas()}
            style={{ textDecoration: "none" }}
          >
            <Icone nome={i} />
            {n}
          </Link>
        ))}
      </nav>
      <Folhas />
    </div>
  );
}

const raiz = createRootRoute({ component: Casca });
const r = <P extends string>(path: P, component: () => React.ReactNode) => createRoute({ getParentRoute: () => raiz, path, component });
const arvore = raiz.addChildren([
  r("/", TelaInicio),
  r("/doses", TelaDoses),
  r("/toxicos", TelaToxicos),
  r("/receita", TelaReceita),
  r("/mais", TelaMais),
  r("/m/$sub", TelaSub),
  r("/m/mapas/$id", TelaMapa),
]);

export const router = createRouter({ routeTree: arvore, defaultNotFoundComponent: TelaInicio });
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
